/**
 * Para onde voltar depois do login, quando quem interrompeu foi um 401.
 *
 * A guarda de rota (`RequireAuth`) passa o destino pelo `state` do router, que
 * é o caminho bom. Mas o 401 nasce em `lib/http.ts`, **fora** da árvore do
 * React Router: não há `navigate` nem `location` ali. Daí o `sessionStorage`,
 * que sobrevive ao reload duro que a interceptação faz e morre com a aba.
 *
 * `search` e `hash` fazem parte do destino e não são opcionais: o catálogo
 * carrega filtros e paginação na query string, e o link do WhatsApp pode
 * chegar com parâmetros. Devolver o cliente ao catálogo certo com os filtros
 * zerados é um bug, não um detalhe.
 */

const KEY = 'clearance-redirect-after-login';

export const LOGIN_PATH = '/login';

/** Caminho atual completo — o que o usuário veria na barra de endereços. */
export function currentPath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

export function saveRedirectTarget(path: string = currentPath()): void {
  // Guardar `/login` faria o pós-login cair nele mesmo.
  if (path.startsWith(LOGIN_PATH)) return;
  try {
    window.sessionStorage.setItem(KEY, path);
  } catch {
    // Modo privativo/quota: perder o destino é aceitável, quebrar o login não.
  }
}

/** Lê e **consome** o destino — chamada duas vezes não repete o redirect. */
export function takeRedirectTarget(): string | null {
  try {
    const target = window.sessionStorage.getItem(KEY);
    if (target) window.sessionStorage.removeItem(KEY);
    return target;
  } catch {
    return null;
  }
}

/** O `location` que o React Router entrega no `state` da navegação. */
export interface RedirectOrigin {
  pathname?: string;
  search?: string;
  hash?: string;
}

/**
 * Destino final do pós-login, com as duas origens em um lugar só.
 *
 * Precedência: o `state` do router vence, porque descreve a navegação que
 * acabou de ser interrompida; o `sessionStorage` é o resíduo de um 401
 * anterior e pode estar velho. O fallback é a home.
 *
 * Função pura em cima de `state` — o acesso ao `sessionStorage` fica no
 * `takeRedirectTarget`, que já trata browser sem storage.
 */
export function resolveLoginRedirect(
  state: unknown,
  storedTarget: string | null
): string {
  const from = (state as { from?: RedirectOrigin } | null)?.from;
  if (from?.pathname) {
    // `search` e `hash` não são enfeite: o catálogo carrega filtros e
    // paginação na query string. Voltar sem eles é voltar para outra tela.
    return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
  }
  if (storedTarget) return storedTarget;
  return '/';
}
